import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';

import { type IngredientGroupInput, splitIngredientColumns } from './recipeDetail.util';

export interface RecipeIngredientsProps {
  groups: IngredientGroupInput[];
  className?: string;
}

/**
 * A recipe's ingredients, one sub-section per vendor ingredient group. Most of the catalog ships a
 * single unnamed group, which renders as a plain flat list; the ~9% with named groups ("For the
 * sauce") get a sub-heading each.
 *
 * Past `INGREDIENT_COLUMN_AT` items the list balances itself across two columns — see
 * `splitIngredientColumns` for how groups are kept whole. A recipe with no ingredients renders
 * nothing at all rather than a placeholder.
 *
 * The two-column breakpoint is a container query against the *page's* `@container`, matching the
 * design: this section sits in a half-width column, so it goes two-up only once the page itself is
 * wide enough to afford it. Rendered outside a container it stays single-column.
 */
export function RecipeIngredients({ groups, className }: RecipeIngredientsProps) {
  const { t } = useTranslation('shared');
  const columns = splitIngredientColumns(groups);

  if (columns.length === 0) return null;

  return (
    <section data-slot="recipe-ingredients" className={cn('flex flex-col gap-4', className)}>
      <h2 className="text-h4 font-semibold text-foreground">{t('recipes.detail.ingredients.title')}</h2>
      <div className={cn('grid grid-cols-1 gap-x-10', columns.length > 1 && '@[900px]:grid-cols-2')}>
        {columns.map((column, columnIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a column is a positional half of one static list
          <div key={columnIndex} className="flex flex-col gap-4">
            {column.map((group, groupIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: vendor groups carry no id, and names may be null
              <div key={groupIndex} className="flex flex-col gap-2">
                {group.name && (
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.name}</h3>
                )}
                <ul className="flex flex-col">
                  {group.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 border-b border-border py-3 last:border-b-0">
                      <span aria-hidden="true" className="mt-2.5 size-1.5 shrink-0 rounded-full bg-primary" />
                      <span className="flex-1 text-base leading-relaxed text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
